import { createContext, useContext, useRef, useState, type ReactNode, type RefObject } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  ResponsiveContainer,
  Sankey,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { compactNumber } from "@/lib/tobis-market";
import { senderColor } from "@/lib/tobis-sender-colors";

// Chart-Bausteine fuer das Markt-Dashboard. Serien-Unterscheidung INNERHALB
// einer Kachel bleibt monochrom (siehe docs/ive-design-principles.md:
// "Chromatic colour is reserved for delete/destructive actions. Everything
// else stays monochrome.") -- ueber Strichmuster statt Farbe, siehe
// LINE_DASH_PATTERNS. Mehrere Kategorien werden als Small Multiples (mehrere
// Kacheln) statt als mehrfarbige Serien in einem Chart dargestellt.
//
// Bewusste Ausnahme (Marvin, 2026-08-07): JEDE SectionCard bekommt ueber
// SECTION_COLOR_PALETTE einen eigenen, gedeckten Farbton fuer ihre
// Graph-Elemente (Linien/Flaechen/Balken), damit man auf der langen
// Markt-Seite eine Kachel von der naechsten unterscheidet. Die Farbe gilt
// pro Box einheitlich, nicht pro Serie -- das Monochrom-Prinzip innerhalb
// einer Kachel bleibt unberuehrt.
const INK = "rgba(0,0,0,0.85)";
const GRID = "rgba(0,0,0,0.08)";
const AXIS_TEXT = "rgba(0,0,0,0.45)";

// Acht gedeckte, editoriale Toene statt einer kraeftig-bunten kategorialen
// Palette -- passend zum warmen Papier-Hintergrund (--color-surface:
// #faf9f6) und bewusst ohne reines Rot (in diesem Designsystem fuer
// destruktive Aktionen reserviert, siehe docs/ive-design-principles.md).
// Reihenfolge fest (nicht pro Aufruf neu gewuerfelt), damit eine Kachel bei
// jedem Render dieselbe Farbe behaelt; wiederholt sich ab der neunten Box.
// Heller/luftiger als eine erste Fassung (Marvin, 2026-08-07): gleicher
// Farbton, mehr Licht (+Lightness, -Saturation), damit es auf dem hellen
// Papier-Hintergrund nicht so dunkel/schwer wirkt.
export const SECTION_COLOR_PALETTE = [
  "#5a8c74", // Salbeigruen
  "#558caa", // Blau
  "#be7a55", // Terracotta
  "#c8a641", // Ocker
  "#93628a", // Pflaume
  "#53938f", // Petrol
  "#a86e48", // Umbra
  "#6f7d9b", // Schiefergrau
] as const;

const SectionColorContext = createContext<string>(INK);

export function SectionColorProvider({
  colorIndex,
  children,
}: {
  colorIndex: number;
  children: ReactNode;
}) {
  const color = SECTION_COLOR_PALETTE[colorIndex % SECTION_COLOR_PALETTE.length] ?? INK;
  return <SectionColorContext.Provider value={color}>{children}</SectionColorContext.Provider>;
}

function useChartColor(): string {
  return useContext(SectionColorContext);
}

function TooltipCard({ label, value, extra }: { label: string; value: string; extra?: ReactNode }) {
  return (
    <div className="box-flat bg-surface px-3 py-2 text-[12px] shadow-sm">
      <div className="text-ink/55">{label}</div>
      <div className="mt-0.5 tabular-nums text-ink">{value}</div>
      {extra}
    </div>
  );
}

/**
 * Recharts' Hover-Tooltip hat `pointer-events: none` (die Maus muss durch ihn
 * hindurch zum Chart koennen) -- Links/Bilder darin waeren nie klickbar. Wenn
 * `tooltipExtra` gesetzt ist (Top-Filme mit Link+Poster), pinnt ein Klick auf
 * einen Punkt deshalb ein EIGENES, normales Div an dessen Position, das
 * bestehen bleibt bis man es schliesst -- unabhaengig vom recharts-Tooltip.
 *
 * Position kommt bewusst NICHT aus `activeCoordinate` (der rohen Datenpunkt-
 * Koordinate): recharts positioniert seinen eigenen Hover-Tooltip ueber einen
 * internen, nicht oeffentlichen Algorithmus (Rand-Vermeidung, Versatz vom
 * Cursor) - eine eigene Neuberechnung daraus landet an einer sichtbar
 * anderen Stelle als der Hover-Tooltip, den man gerade gesehen hat (der
 * "Sprung"-Bug). Stattdessen wird im Klick-Moment die Position des GERADE
 * angezeigten `.recharts-tooltip-wrapper` direkt aus dem DOM gelesen -- exakt
 * dieselbe Stelle, pixelgenau, unabhaengig davon, wie recharts intern rechnet.
 */
function usePinnedPoint(containerRef: RefObject<HTMLDivElement | null>) {
  const [pinned, setPinned] = useState<{ label: unknown; x: number; y: number } | null>(null);
  const onClick = (state: { activeLabel?: unknown }) => {
    if (state?.activeLabel === undefined) return;
    const container = containerRef.current;
    const tooltipEl = container?.querySelector<HTMLElement>(".recharts-tooltip-wrapper");
    if (!container || !tooltipEl) return;
    const containerRect = container.getBoundingClientRect();
    const tooltipRect = tooltipEl.getBoundingClientRect();
    setPinned({
      label: state.activeLabel,
      x: tooltipRect.left - containerRect.left,
      y: tooltipRect.top - containerRect.top,
    });
  };
  return { pinned, onClick, close: () => setPinned(null) };
}

function PinnedPanel({
  pinned,
  onClose,
  label,
  value,
  extra,
}: {
  pinned: { x: number; y: number };
  onClose: () => void;
  label: string;
  value: string;
  extra?: ReactNode;
}) {
  return (
    <div className="absolute z-10" style={{ left: pinned.x, top: pinned.y }}>
      <div className="box-flat relative bg-surface px-3 py-2 text-[12px] shadow-sm">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-1 top-1 text-ink/40 hover:text-ink"
        >
          ×
        </button>
        <div className="pr-4 text-ink/55">{label}</div>
        <div className="mt-0.5 tabular-nums text-ink">{value}</div>
        {extra}
      </div>
    </div>
  );
}

/**
 * Recharts' YAxis reserviert genau `width` Pixel fuer die Tick-Labels; ein zu
 * kleiner fester Wert (vorher 48) schneidet lange Labels ("180 Mio.", "1,2
 * Mrd. €") links ab. Das sah wie eine kaputte, nicht-monoton sortierte Skala
 * aus ("80 Mio." statt "180 Mio."), war aber reines Clipping, kein Datenfehler.
 * Breite deshalb aus den tatsaechlichen Werten schaetzen statt raten.
 */
function estimateYAxisWidth<T>(data: T[], key: keyof T, formatY: (v: number) => string): number {
  let maxLen = 0;
  for (const row of data) {
    const raw = row[key];
    if (typeof raw !== "number") continue;
    const len = formatY(raw).length;
    if (len > maxLen) maxLen = len;
  }
  return Math.max(40, Math.round(maxLen * 6.5) + 14);
}

export function LineStat<T>({
  data,
  xKey,
  yKey,
  valueLabel,
  formatX,
  formatY = compactNumber,
  height = 180,
  tooltipExtra,
}: {
  data: T[];
  xKey: keyof T;
  yKey: keyof T;
  valueLabel: string;
  formatX?: (v: unknown) => string;
  formatY?: (v: number) => string;
  height?: number;
  /** Zusaetzlicher Tooltip-Inhalt unter dem Wert (z.B. Top-Filme des Jahres), bekommt das rohe xKey-Label. */
  tooltipExtra?: (label: unknown) => ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { pinned, onClick, close } = usePinnedPoint(containerRef);
  const color = useChartColor();
  if (data.length === 0) return <EmptyChart height={height} />;
  const pinnedValue = pinned && data.find((d) => d[xKey] === pinned.label)?.[yKey];
  return (
    <div className="relative" ref={containerRef}>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} onClick={tooltipExtra ? onClick : undefined}>
          <CartesianGrid vertical={false} stroke={GRID} strokeWidth={1} />
          <XAxis
            dataKey={xKey as string}
            tickFormatter={formatX}
            tick={{ fontSize: 11, fill: AXIS_TEXT }}
            axisLine={{ stroke: GRID }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v: number) => formatY(v)}
            tick={{ fontSize: 11, fill: AXIS_TEXT }}
            axisLine={false}
            tickLine={false}
            width={estimateYAxisWidth(data, yKey, formatY)}
          />
          <Tooltip
            content={({ active, payload, label }) =>
              active && payload?.[0] && !pinned ? (
                <TooltipCard
                  label={formatX ? formatX(label) : String(label)}
                  value={`${valueLabel}: ${formatY(Number(payload[0].value))}`}
                  extra={tooltipExtra?.(label)}
                />
              ) : null
            }
          />
          <Line
            type="monotone"
            dataKey={yKey as string}
            stroke={color}
            strokeWidth={2}
            dot={data.length <= 24}
            activeDot={{ r: 4, stroke: "var(--color-surface)", strokeWidth: 2, fill: color }}
            isAnimationActive={false}
            style={tooltipExtra ? { cursor: "pointer" } : undefined}
          />
        </LineChart>
      </ResponsiveContainer>
      {pinned && tooltipExtra && typeof pinnedValue === "number" && (
        <PinnedPanel
          pinned={pinned}
          onClose={close}
          label={formatX ? formatX(pinned.label) : String(pinned.label)}
          value={`${valueLabel}: ${formatY(pinnedValue)}`}
          extra={tooltipExtra(pinned.label)}
        />
      )}
    </div>
  );
}

// Strichmuster statt Farbe fuer die Serien-Identitaet, damit die Monochrom-
// Konvention (siehe Modulkommentar) auch bei mehreren Linien im selben Chart
// haelt. Reihenfolge fest (nicht pro Aufruf neu gewuerfelt), damit dieselbe
// Serie ueber verschiedene Charts hinweg immer dasselbe Muster hat. Fuenf
// Muster (statt drei) fuer Charts mit bis zu fuenf Serien (z.B. Genre-Top-4 +
// Sonstige) - ab der sechsten Serie wiederholt sich das erste Muster, dann
// lieber die Serien reduzieren als noch feinere Dashes erzwingen.
const LINE_DASH_PATTERNS = [undefined, "6 4", "2 3", "1 4", "8 2 2 2"] as const;

export function MultiLineStat<T>({
  data,
  xKey,
  series,
  formatX,
  formatY = compactNumber,
  height = 220,
}: {
  data: T[];
  xKey: keyof T;
  series: { key: keyof T; label: string }[];
  formatX?: (v: unknown) => string;
  formatY?: (v: number) => string;
  height?: number;
  /**
   * Faerbt jeden Balken nach seiner Kategorie statt einheitlich in der
   * Kachelfarbe. Nur fuer Sender-Ranglisten gedacht: dort ist die Kategorie
   * dieselbe Groesse, die im Tagesdiagramm darueber die Segmente traegt, und
   * zwei verschiedene Farbsprachen fuer dieselben Sender auf einer Seite waeren
   * schlechter als gar keine.
   */
  colorByCategory?: boolean;
}) {
  const color = useChartColor();
  if (data.length === 0) return <EmptyChart height={height} />;
  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <LineChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={GRID} strokeWidth={1} />
          <XAxis
            dataKey={xKey as string}
            tickFormatter={formatX}
            tick={{ fontSize: 11, fill: AXIS_TEXT }}
            axisLine={{ stroke: GRID }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v: number) => formatY(v)}
            tick={{ fontSize: 11, fill: AXIS_TEXT }}
            axisLine={false}
            tickLine={false}
            width={Math.max(...series.map((s) => estimateYAxisWidth(data, s.key, formatY)))}
          />
          <Tooltip
            content={({ active, payload, label }) =>
              active && payload?.length ? (
                <div className="box-flat bg-surface px-3 py-2 text-[12px] shadow-sm">
                  <div className="text-ink/55">{formatX ? formatX(label) : String(label)}</div>
                  {series.map((s) => {
                    const point = payload.find((p) => p.dataKey === s.key);
                    if (!point) return null;
                    return (
                      <div key={s.label} className="mt-0.5 tabular-nums text-ink">
                        {s.label}: {formatY(Number(point.value))}
                      </div>
                    );
                  })}
                </div>
              ) : null
            }
          />
          {series.map((s, i) => (
            <Line
              key={s.label}
              type="monotone"
              dataKey={s.key as string}
              stroke={color}
              strokeWidth={2}
              strokeDasharray={LINE_DASH_PATTERNS[i % LINE_DASH_PATTERNS.length]}
              dot={false}
              activeDot={{ r: 4, stroke: "var(--color-surface)", strokeWidth: 2, fill: color }}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
        {series.map((s, i) => (
          <div key={s.label} className="flex items-center gap-1.5 text-[11px] text-ink/55">
            <svg width="16" height="8" aria-hidden="true">
              <line
                x1="0"
                y1="4"
                x2="16"
                y2="4"
                stroke={color}
                strokeWidth={2}
                strokeDasharray={LINE_DASH_PATTERNS[i % LINE_DASH_PATTERNS.length]}
              />
            </svg>
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}

export function AreaStat<T>({
  data,
  xKey,
  yKey,
  valueLabel,
  formatX,
  formatY = compactNumber,
  height = 180,
  tooltipExtra,
}: {
  data: T[];
  xKey: keyof T;
  yKey: keyof T;
  valueLabel: string;
  formatX?: (v: unknown) => string;
  formatY?: (v: number) => string;
  height?: number;
  /** Zusaetzlicher Tooltip-Inhalt unter dem Wert (z.B. Top-Filme des Jahres), bekommt das rohe xKey-Label. */
  tooltipExtra?: (label: unknown) => ReactNode;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { pinned, onClick, close } = usePinnedPoint(containerRef);
  const color = useChartColor();
  if (data.length === 0) return <EmptyChart height={height} />;
  const pinnedValue = pinned && data.find((d) => d[xKey] === pinned.label)?.[yKey];
  return (
    <div className="relative" ref={containerRef}>
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }} onClick={tooltipExtra ? onClick : undefined}>
          <CartesianGrid vertical={false} stroke={GRID} strokeWidth={1} />
          <XAxis
            dataKey={xKey as string}
            tickFormatter={formatX}
            tick={{ fontSize: 11, fill: AXIS_TEXT }}
            axisLine={{ stroke: GRID }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v: number) => formatY(v)}
            tick={{ fontSize: 11, fill: AXIS_TEXT }}
            axisLine={false}
            tickLine={false}
            width={estimateYAxisWidth(data, yKey, formatY)}
          />
          <Tooltip
            content={({ active, payload, label }) =>
              active && payload?.[0] && !pinned ? (
                <TooltipCard
                  label={formatX ? formatX(label) : String(label)}
                  value={`${valueLabel}: ${formatY(Number(payload[0].value))}`}
                  extra={tooltipExtra?.(label)}
                />
              ) : null
            }
          />
          <Area
            type="monotone"
            dataKey={yKey as string}
            stroke={color}
            strokeWidth={2}
            fill={color}
            fillOpacity={0.1}
            isAnimationActive={false}
            style={tooltipExtra ? { cursor: "pointer" } : undefined}
          />
        </AreaChart>
      </ResponsiveContainer>
      {pinned && tooltipExtra && typeof pinnedValue === "number" && (
        <PinnedPanel
          pinned={pinned}
          onClose={close}
          label={formatX ? formatX(pinned.label) : String(pinned.label)}
          value={`${valueLabel}: ${formatY(pinnedValue)}`}
          extra={tooltipExtra(pinned.label)}
        />
      )}
    </div>
  );
}

/**
 * Verlauf als vertikale Balken ueber eine kategorische Zeitachse - fuer
 * Staende zu unregelmaessigen Zeitpunkten (z.B. Abrufdaten), wo eine Linie
 * eine Kontinuitaet zwischen den Punkten suggerieren wuerde, die es nicht
 * gibt. BarCompare bleibt das Gegenstueck fuer Kategorien-Vergleiche
 * (horizontal, eine Zeile je Kategorie).
 */
export function BarStat<T>({
  data,
  xKey,
  yKey,
  valueLabel,
  formatX,
  formatY = compactNumber,
  height = 200,
}: {
  data: T[];
  xKey: keyof T;
  yKey: keyof T;
  valueLabel: string;
  formatX?: (v: unknown) => string;
  formatY?: (v: number) => string;
  height?: number;
  /**
   * Faerbt jeden Balken nach seiner Kategorie statt einheitlich in der
   * Kachelfarbe. Nur fuer Sender-Ranglisten gedacht: dort ist die Kategorie
   * dieselbe Groesse, die im Tagesdiagramm darueber die Segmente traegt, und
   * zwei verschiedene Farbsprachen fuer dieselben Sender auf einer Seite waeren
   * schlechter als gar keine.
   */
  colorByCategory?: boolean;
}) {
  const color = useChartColor();
  if (data.length === 0) return <EmptyChart height={height} />;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke={GRID} strokeWidth={1} />
        <XAxis
          dataKey={xKey as string}
          tickFormatter={formatX}
          tick={{ fontSize: 11, fill: AXIS_TEXT }}
          axisLine={{ stroke: GRID }}
          tickLine={false}
        />
        <YAxis
          tickFormatter={(v: number) => formatY(v)}
          tick={{ fontSize: 11, fill: AXIS_TEXT }}
          axisLine={false}
          tickLine={false}
          width={estimateYAxisWidth(data, yKey, formatY)}
        />
        <Tooltip
          cursor={{ fill: GRID }}
          content={({ active, payload, label }) =>
            active && payload?.[0] ? (
              <TooltipCard
                label={formatX ? formatX(label) : String(label)}
                value={`${valueLabel}: ${formatY(Number(payload[0].value))}`}
              />
            ) : null
          }
        />
        <Bar dataKey={yKey as string} fill={color} radius={[4, 4, 0, 0]} maxBarSize={48} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function BarCompare<T>({
  data,
  xKey,
  yKey,
  valueLabel,
  formatY = compactNumber,
  height = 220,
  colorByCategory = false,
}: {
  data: T[];
  xKey: keyof T;
  yKey: keyof T;
  valueLabel: string;
  formatY?: (v: number) => string;
  height?: number;
  /**
   * Faerbt jeden Balken nach seiner Kategorie statt einheitlich in der
   * Kachelfarbe. Nur fuer Sender-Ranglisten gedacht: dort ist die Kategorie
   * dieselbe Groesse, die im Tagesdiagramm darueber die Segmente traegt, und
   * zwei verschiedene Farbsprachen fuer dieselben Sender auf einer Seite waeren
   * schlechter als gar keine.
   */
  colorByCategory?: boolean;
}) {
  const color = useChartColor();
  if (data.length === 0) return <EmptyChart height={height} />;
  // Genug Zeilenhoehe fuer jede Kategorie, sonst ueberspringt recharts'
  // automatische Tick-Kollisionsvermeidung jede zweite Achsenbeschriftung
  // (16 Bundeslaender in der festen Default-Hoehe zeigten nur noch 8 Namen).
  // 20px/Zeile statt vorher 32px -- kompakter, damit lange Listen (42 Staedte)
  // nicht die Seite sprengen, aber noch genug fuer kollisionsfreie Labels.
  const resolvedHeight = Math.max(height, data.length * 20 + 16);
  return (
    <ResponsiveContainer width="100%" height={resolvedHeight}>
      <BarChart data={data} layout="vertical" margin={{ top: 0, right: 24, bottom: 0, left: 0 }}>
        <CartesianGrid horizontal={false} stroke={GRID} strokeWidth={1} />
        <XAxis
          type="number"
          tickFormatter={(v: number) => formatY(v)}
          tick={{ fontSize: 11, fill: AXIS_TEXT }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          type="category"
          dataKey={xKey as string}
          tick={{ fontSize: 11, fill: AXIS_TEXT }}
          axisLine={false}
          tickLine={false}
          width={130}
          interval={0}
        />
        <Tooltip
          content={({ active, payload }) =>
            active && payload?.[0] ? (
              <TooltipCard
                label={String(payload[0].payload[xKey as string])}
                value={`${valueLabel}: ${formatY(Number(payload[0].value))}`}
              />
            ) : null
          }
        />
        <Bar dataKey={yKey as string} fill={color} radius={[0, 4, 4, 0]} maxBarSize={14} isAnimationActive={false}>
          {colorByCategory
            ? data.map((d, i) => (
                <Cell key={i} fill={senderColor(String(d[xKey as keyof T] ?? ""))} />
              ))
            : null}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Gestapelte Tagesbalken: ein Balken je Tag, aufgeteilt nach Sender.
 *
 * Warum ueberhaupt: die Tages-Top-10 wechselt taeglich ihre Sendungen, die
 * Sender bleiben dieselben. In getrennten Tagestabellen faellt deshalb nicht
 * auf, dass am 14.08.2026 die ARD mit 19,18 Mio vor dem ZDF (13,80) lag,
 * waehrend das ZDF an allen anderen erfassten Tagen fuehrt.
 *
 * Die Segmente tragen feste Senderfarben (siehe lib/tobis-sender-colors.ts).
 * Erst als Deckkraft-Abstufung EINES Farbtons gebaut, um im Monochrom-Prinzip
 * zu bleiben - in der Praxis liessen sich drei Violettstufen aber nicht
 * auseinanderhalten, und eine Legende, die man bei jedem Balken neu
 * nachschlaegt, kostet mehr als die Farbe einbringt (Marvin, 2026-08-17).
 *
 * Die Stapelreihenfolge steht fest ueber alle Balken (siehe tvDayStack), sonst
 * waeren die Flaechen nicht vergleichbar.
 */
/** Was StackedDayBars von einer Sendung im Tooltip braucht - absichtlich
 *  strukturell und nicht als Import aus lib: die Komponente soll mit jeder
 *  Datenquelle funktionieren, die diese drei Felder liefert. */
interface ShowLike {
  titel: string;
  startzeit?: string;
  mio: number;
}

export function StackedDayBars({
  data,
  segments,
  xKey = "date",
  valueLabel,
  formatX,
  formatY = compactNumber,
  height = 260,
}: {
  // Der Wertetyp schliesst ShowLike[] mit ein, weil der Datenpunkt neben den
  // Segmentzahlen die Sendungen je Segment traegt (Feld 'shows', siehe
  // Tooltip). Ohne diesen Zweig passt TvDayBar nicht auf die Prop.
  data: Array<Record<string, string | number | Record<string, ShowLike[]>>>;
  /** Segmentnamen in Stapelreihenfolge, der reichweitenstaerkste zuerst. */
  segments: readonly string[];
  xKey?: string;
  valueLabel: string;
  formatX?: (v: unknown) => string;
  formatY?: (v: number) => string;
  height?: number;
}) {
  if (data.length === 0 || segments.length === 0) return <EmptyChart height={height} />;

  return (
    <div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={GRID} strokeWidth={1} />
          <XAxis
            dataKey={xKey}
            tickFormatter={formatX}
            tick={{ fontSize: 11, fill: AXIS_TEXT }}
            axisLine={{ stroke: GRID }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={(v: number) => formatY(v)}
            tick={{ fontSize: 11, fill: AXIS_TEXT }}
            axisLine={false}
            tickLine={false}
            width={44}
          />
          <Tooltip
            cursor={{ fill: GRID }}
            content={({ active, payload, label }) =>
              active && payload && payload.length > 0 ? (
                <TooltipCard
                  label={formatX ? formatX(label) : String(label)}
                  value={`${valueLabel}: ${formatY(
                    payload.reduce((sum, p) => sum + Number(p.value ?? 0), 0),
                  )}`}
                  extra={
                    // Breite gedeckelt, sonst zieht ein Titel wie
                    // "RHYTHMISCHE SPORTGYMNASTIK WM" den Tooltip ueber das
                    // halbe Diagramm - erst dadurch greift das truncate unten.
                    <span className="block max-w-[16rem]">
                      {/* Absteigend, damit die Reihenfolge im Tooltip der im
                          Balken entspricht - recharts liefert sie von unten. */}
                      {[...payload].reverse().map((p) => {
                        const name = String(p.dataKey);
                        // Die Sendungen des Segments haengen am Datenpunkt
                        // (tvDayStack), nicht am einzelnen Balkenstueck.
                        const shows = (p.payload as { shows?: Record<string, ShowLike[]> })?.shows?.[name];
                        return (
                          <span key={name} className="mt-1.5 block first:mt-0">
                            <span className="flex items-center gap-1.5">
                              <span
                                aria-hidden
                                className="inline-block h-2 w-2 shrink-0 rounded-[2px]"
                                style={{ backgroundColor: senderColor(name) }}
                              />
                              <span className="text-ink/80">
                                {name}: {formatY(Number(p.value ?? 0))}
                              </span>
                            </span>
                            {shows?.length ? (
                              <span className="mt-0.5 block pl-3.5 text-ink/45">
                                {shows.map((sh, i) => (
                                  <span key={`${sh.titel}-${i}`} className="block truncate">
                                    {sh.titel}
                                    {sh.startzeit ? ` ${sh.startzeit}` : ""} · {formatY(sh.mio)}
                                  </span>
                                ))}
                              </span>
                            ) : null}
                          </span>
                        );
                      })}
                    </span>
                  }
                />
              ) : null
            }
          />
          {segments.map((name, i) => (
            <Bar
              key={name}
              dataKey={name}
              stackId="tag"
              fill={senderColor(name)}
              isAnimationActive={false}
              // Nur das oberste Segment bekommt runde Ecken, sonst schneidet
              // jede Stapelgrenze eine sichtbare Kerbe in den Balken.
              radius={i === segments.length - 1 ? [4, 4, 0, 0] : undefined}
              maxBarSize={48}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
      {/* Eigene Legende statt der von recharts: die zeigt farbige Punkte, hier
          traegt aber die Deckkraft die Unterscheidung. */}
      <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink/55">
        {segments.map((name) => (
          <li key={name} className="flex items-center gap-1.5">
            <span
              aria-hidden
              className="inline-block h-2.5 w-2.5 rounded-[2px]"
              style={{ backgroundColor: senderColor(name) }}
            />
            {name}
          </li>
        ))}
      </ul>
    </div>
  );
}

export interface SankeyNodeDatum {
  name: string;
}

export interface SankeyLinkDatum {
  source: number;
  target: number;
  value: number;
}

/**
 * Sankey mit eigenem Node-Renderer: recharts' Default-Node zeichnet nur ein
 * Rechteck ohne Beschriftung, deshalb Name+Wert als Text daneben. Links sind
 * reine Flaechen in der Box-Farbe ohne Verlauf (Standard-Sankey faerbt Links
 * nach Quellknoten -- das waere mehrfarbig innerhalb einer einzelnen Kachel
 * und wuerde damit dem Small-Multiples-Prinzip widersprechen, siehe
 * Modulkommentar).
 */
export function SankeyStat({
  data,
  formatY = compactNumber,
  height = 320,
}: {
  data: { nodes: SankeyNodeDatum[]; links: SankeyLinkDatum[] };
  formatY?: (v: number) => string;
  height?: number;
}) {
  const color = useChartColor();
  if (data.nodes.length === 0 || data.links.length === 0) return <EmptyChart height={height} />;
  return (
    <ResponsiveContainer width="100%" height={height} className="max-w-3xl">
      <Sankey
        data={data}
        // Standard ist "justify": Knoten ohne eigene Kinder (z.B. SVoD, das
        // nicht weiter nach Format aufgeschluesselt ist) werden dabei auf die
        // tiefste Spalte gezwungen, egal auf welcher Ebene sie eigentlich
        // sitzen -- der Link laeuft dann als sehr flacher Bogen quer durch
        // den ganzen Chart und zieht ihn unnoetig breit auseinander. "left"
        // laesst jeden Knoten auf seiner tatsaechlichen Tiefe.
        align="left"
        node={({ x, y, width, height: nodeHeight, payload }) => {
          const isLeft = x < 40;
          return (
            <g>
              <rect x={x} y={y} width={width} height={nodeHeight} fill={color} />
              <text
                x={isLeft ? x - 6 : x + width + 6}
                y={y + nodeHeight / 2}
                textAnchor={isLeft ? "end" : "start"}
                dominantBaseline="middle"
                fontSize={11}
                fill={AXIS_TEXT}
              >
                {payload.name} · {formatY(payload.value)}
              </text>
            </g>
          );
        }}
        link={{ stroke: "none", fill: color, fillOpacity: 0.12 }}
        nodePadding={24}
        nodeWidth={10}
        margin={{ top: 8, right: 140, bottom: 8, left: 140 }}
      >
        <Tooltip
          content={({ active, payload }) => {
            if (!active || !payload?.[0]) return null;
            const item = payload[0].payload;
            if (item && "source" in item && "target" in item) {
              return (
                <TooltipCard
                  label={`${item.source.name} → ${item.target.name}`}
                  value={formatY(Number(item.value))}
                />
              );
            }
            return null;
          }}
        />
      </Sankey>
    </ResponsiveContainer>
  );
}

function EmptyChart({ height }: { height: number }) {
  return (
    <div className="flex items-center justify-center text-[12px] text-ink/40" style={{ height }}>
      —
    </div>
  );
}
